import { SIX_TOPIC } from '@/config/networkConstants';
import type { ConnectionInfo, TurnConfig } from '@/types/poc/libp2p';
import { incomingMessageReceived } from '@/store/actions/poc/incomingMessageReceived';
import { selectCurrentUserId } from '@/store/selectors/auth';
import { selectMessageBrokerMultiaddr } from '@/store/selectors/organization';
import { createAppAsyncThunk } from '@/store/thunks/createAppAsyncThunk';
import { getConnectionInfoThunk } from '@/store/thunks/poc/connection/getConnectionInfoThunk';
import { extractPeerIdFromMultiaddr } from '@/utils/p2p.utils';

interface InitializeP2PStackArgs {
  sixId: string;
  bootstrapNodes: string[];
  turnConfig?: TurnConfig;
}

interface InitializeP2PStackResult {
  sixId: string;
  peerId: string;
  peers: number;
  connectionDetails: ConnectionInfo[];
  messagingInitialized: boolean;
  subscribedTopics: string[];
  bootstrapPeerIds: string[];
  messageBrokerPeerId: string | null;
}

export const initializeP2PStackThunk = createAppAsyncThunk<
  InitializeP2PStackResult,
  InitializeP2PStackArgs
>(
  'connection/initializeP2PStack',
  async (options, { dispatch, getState, extra: { pocServices }, rejectWithValue }) => {
    try {
      const state = getState();
      const userId = selectCurrentUserId(state);
      const brokerMultiaddr = selectMessageBrokerMultiaddr(state);

      const allBootstrapNodes = [
        ...new Set([
          ...options.bootstrapNodes,
          ...(brokerMultiaddr != null ? [brokerMultiaddr] : []),
        ]),
      ];

      await pocServices.p2pStackManagerService.startSharedNode({
        bootstrapNodes: allBootstrapNodes,
        userId,
        turnConfig: options.turnConfig,
        onMessage: (result, stats) => {
          dispatch(incomingMessageReceived({ result, stats }));
        },
      });

      pocServices.p2pStackManagerService.ensureBootstrapNodesDialed(allBootstrapNodes);

      pocServices.p2pStackManagerService.subscribeToSix({
        sixId: options.sixId,
        onConnectionUpdate: () => {
          void dispatch(getConnectionInfoThunk({ sixId: options.sixId }));
        },
      });

      const libp2pNode = pocServices.p2pStackManagerService.getLibp2pNode();
      if (libp2pNode === null) {
        throw new Error('LibP2P node not ready after initialization');
      }

      const currentConnections = pocServices.p2pStackManagerService.getConnectionInfo();
      const bootstrapPeerIds = options.bootstrapNodes
        .map(extractPeerIdFromMultiaddr)
        .filter((id): id is string => id != null);

      const messageBrokerPeerId =
        brokerMultiaddr != null ? extractPeerIdFromMultiaddr(brokerMultiaddr) : null;

      return {
        sixId: options.sixId,
        peerId: libp2pNode.peerId.toString(),
        peers: libp2pNode.getPeers().length,
        connectionDetails: currentConnections,
        messagingInitialized: true,
        subscribedTopics: [SIX_TOPIC(options.sixId)],
        bootstrapPeerIds,
        messageBrokerPeerId,
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      return rejectWithValue({ message });
    }
  },
  {
    condition: (options, { getState }) => {
      const existingConnection = getState().connection.connections.find(
        (connection) => connection.sixId === options.sixId
      );

      if (existingConnection == null) {
        return true;
      }

      return (
        existingConnection.status !== 'connecting' && !existingConnection.isP2PStackInitialized
      );
    },
  }
);
